'use client'

import {
    BillingFeePolicy,
    FeeBasis,
    FeeRule,
    MethodFeePolicy,
    getBillingFeePolicy,
    updateBillingFeePolicy,
} from '@/lib/api/financial/financial-settings-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useEffect, useState } from 'react'
import { MdAdd, MdDelete, MdInfoOutline, MdSave } from 'react-icons/md'
import { toast } from 'react-toastify'
import TabBranchActions from './tab-branch-actions'

/**
 * O que a empresa REPASSA ao cliente — não o que a operadora cobra dela (isso é a aba
 * "Taxas Fixas", só leitura).
 *
 * Cada método escolhe uma base: não repassar (a empresa absorve), acompanhar a operadora
 * (o valor segue a tabela da conta, inclusive quando ela é renegociada) ou faixas
 * próprias, que podem ficar acima ou abaixo do custo.
 *
 * A faixa aplica valor fixo e percentual SOMADOS, porque é assim que o provedor cobra
 * ("2,69% + R$ 0,29"): com um só dos dois, ou sobrava para o cliente à vista ou
 * faltava no parcelado.
 */

const currencyFormat = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    return parseInt(unMask(value, 'currency')) || 0
}

/**
 * Percentual digitado. Aceita `3,49` e `3.49`.
 *
 * O campo é `type="number"` e o navegador costuma normalizar o separador do idioma,
 * mas nem todos o fazem: onde não faz, a vírgula devolve string vazia, `parseFloat`
 * dá `NaN` e o percentual seria gravado como ZERO sem nenhum aviso — a cobrança
 * pararia de somar a taxa e ninguém saberia por quê.
 *
 * O backend guarda com DUAS casas (`toPercent` arredonda): `3,49` fica `3.49`,
 * `3,499` vira `3.5`.
 */
const parsePercent = (raw: string): number => {
    const n = parseFloat(String(raw).replace(',', '.'))
    return Number.isFinite(n) ? n : 0
}

/**
 * Zero é um valor, não um campo em branco.
 *
 * Mostrar `R$ 0,00` importa aqui: `0` é como se configura **sem juros** numa faixa (ex.:
 * até 3 parcelas sem taxa). Em branco, o operador digita zero, o campo esvazia e a tela
 * diz que o valor não foi aceito — quando foi, e está gravado. Só `null`/`undefined`
 * (campo nunca preenchido) fica vazio.
 */
const centsToDisplay = (cents: number | null | undefined): string =>
    cents == null ? '' : currencyFormat(String(cents))

type MethodKey = 'pix' | 'creditCard' | 'debitCard' | 'boleto'

const METHODS: { key: MethodKey; label: string; hasInstallments: boolean }[] = [
    { key: 'pix', label: 'Pix', hasInstallments: false },
    { key: 'creditCard', label: 'Cartão de crédito', hasInstallments: true },
    { key: 'debitCard', label: 'Cartão de débito', hasInstallments: false },
    { key: 'boleto', label: 'Boleto', hasInstallments: false },
]

const BASIS_OPTIONS: { value: FeeBasis; label: string; hint: string }[] = [
    {
        value: 'NONE',
        label: 'Não repassar',
        hint: 'A empresa absorve o custo desta forma de pagamento.',
    },
    {
        value: 'PROVIDER',
        label: 'Acompanhar as taxas fixas',
        hint: 'Repassa exatamente o que a operadora cobra. Se a tabela for renegociada, acompanha sozinho.',
    },
    {
        value: 'CUSTOM',
        label: 'Faixas próprias',
        hint: 'Valores definidos por você, que podem ficar acima ou abaixo do custo.',
    },
]

const emptyRule = (): FeeRule => ({
    minAmount: 0,
    maxAmount: null,
    fromInstallment: 1,
    toInstallment: null,
    fixed: 0,
    percent: 0,
})

const DEFAULT_POLICY: BillingFeePolicy = {
    version: 2,
    enabled: false,
    grossUp: false,
    pix: { basis: 'NONE', rules: [] },
    creditCard: { basis: 'NONE', rules: [] },
    debitCard: { basis: 'NONE', rules: [] },
    boleto: { basis: 'NONE', rules: [] },
    payout: { enabled: false, fixed: 0, percent: 0 },
    anticipation: { enabled: false, basis: 'PROVIDER', monthlyPercent: 0 },
}

/** Uma linha de faixa. Dinheiro entra mascarado; percentual, como número. */
function RuleRow({
    rule,
    hasInstallments,
    onChange,
    onRemove,
}: {
    rule: FeeRule
    hasInstallments: boolean
    onChange: (next: FeeRule) => void
    onRemove: () => void
}) {
    const set = (patch: Partial<FeeRule>) => onChange({ ...rule, ...patch })

    return (
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-base-300 p-3 md:grid-cols-12 md:items-end">
            <label className="form-control md:col-span-2">
                <span className="label-text text-xs">Valor de</span>
                <input
                    type="text"
                    className="input input-bordered input-sm"
                    value={centsToDisplay(rule.minAmount)}
                    placeholder="R$ 0,00"
                    onChange={e =>
                        set({
                            minAmount: parseCurrencyToCents(
                                currencyFormat(e.target.value)
                            ),
                        })
                    }
                />
            </label>

            <label className="form-control md:col-span-2">
                <span className="label-text text-xs">Até</span>
                <input
                    type="text"
                    className="input input-bordered input-sm"
                    value={centsToDisplay(rule.maxAmount)}
                    placeholder="Sem limite"
                    onChange={e => {
                        const cents = parseCurrencyToCents(
                            currencyFormat(e.target.value)
                        )
                        set({ maxAmount: cents > 0 ? cents : null })
                    }}
                />
            </label>

            {hasInstallments && (
                <>
                    <label className="form-control md:col-span-2">
                        <span className="label-text text-xs">Parcelas de</span>
                        <input
                            type="number"
                            min={1}
                            className="input input-bordered input-sm"
                            value={rule.fromInstallment}
                            onChange={e =>
                                set({
                                    fromInstallment: Math.max(
                                        1,
                                        parseInt(e.target.value, 10) || 1
                                    ),
                                })
                            }
                        />
                    </label>
                    <label className="form-control md:col-span-2">
                        <span className="label-text text-xs">Até</span>
                        <input
                            type="number"
                            min={1}
                            className="input input-bordered input-sm"
                            value={rule.toInstallment ?? ''}
                            placeholder="Sem limite"
                            onChange={e => {
                                const parsed = parseInt(e.target.value, 10)
                                set({
                                    toInstallment: Number.isFinite(parsed)
                                        ? parsed
                                        : null,
                                })
                            }}
                        />
                    </label>
                </>
            )}

            <label className="form-control md:col-span-2">
                <span className="label-text text-xs">Valor fixo</span>
                <input
                    type="text"
                    className="input input-bordered input-sm"
                    value={centsToDisplay(rule.fixed)}
                    placeholder="R$ 0,00"
                    onChange={e =>
                        set({
                            fixed: parseCurrencyToCents(
                                currencyFormat(e.target.value)
                            ),
                        })
                    }
                />
            </label>

            <label className="form-control md:col-span-2">
                <span className="label-text text-xs">Percentual</span>
                <label className="input input-bordered input-sm flex items-center gap-1">
                    <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        className="w-full bg-transparent outline-none"
                        // Pelo mesmo motivo do valor fixo: `0` é "sem juros nesta
                        // faixa", e some da tela se for tratado como vazio.
                        value={rule.percent ?? ''}
                        placeholder="0,00"
                        onChange={e =>
                            set({ percent: parsePercent(e.target.value) })
                        }
                    />
                    <span className="text-xs opacity-60">%</span>
                </label>
            </label>

            <div
                className={hasInstallments ? 'md:col-span-12' : 'md:col-span-4'}
            >
                <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 text-error"
                    onClick={onRemove}
                >
                    <MdDelete className="text-sm" />
                    Remover faixa
                </button>
            </div>
        </div>
    )
}

function MethodCard({
    label,
    hasInstallments,
    value,
    onChange,
}: {
    label: string
    hasInstallments: boolean
    value: MethodFeePolicy
    onChange: (next: MethodFeePolicy) => void
}) {
    const basisHint = BASIS_OPTIONS.find(o => o.value === value.basis)?.hint

    return (
        <div className="rounded-xl border border-base-300 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold">{label}</h3>
                <div className="join">
                    {BASIS_OPTIONS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`btn join-item btn-sm ${
                                value.basis === option.value
                                    ? 'btn-secondary'
                                    : 'btn-outline'
                            }`}
                            onClick={() =>
                                onChange({
                                    ...value,
                                    basis: option.value,
                                    // Primeira faixa já criada: base "própria" sem
                                    // nenhuma faixa não cobra nada, e o operador
                                    // fica sem entender por quê.
                                    rules:
                                        option.value === 'CUSTOM' &&
                                        !value.rules.length
                                            ? [emptyRule()]
                                            : value.rules,
                                })
                            }
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {basisHint && (
                <p className="mt-2 text-xs text-base-content/60">{basisHint}</p>
            )}

            {value.basis === 'CUSTOM' && (
                <div className="mt-3 flex flex-col gap-2">
                    {value.rules.map((rule, index) => (
                        <RuleRow
                            key={index}
                            rule={rule}
                            hasInstallments={hasInstallments}
                            onChange={next =>
                                onChange({
                                    ...value,
                                    rules: value.rules.map((r, i) =>
                                        i === index ? next : r
                                    ),
                                })
                            }
                            onRemove={() =>
                                onChange({
                                    ...value,
                                    rules: value.rules.filter(
                                        (_, i) => i !== index
                                    ),
                                })
                            }
                        />
                    ))}

                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-1 self-start"
                        onClick={() =>
                            onChange({
                                ...value,
                                rules: [...value.rules, emptyRule()],
                            })
                        }
                    >
                        <MdAdd className="text-base" />
                        Adicionar faixa
                    </button>

                    {value.rules.length > 1 && (
                        <p className="text-xs text-base-content/50">
                            A primeira faixa que servir para o valor
                            {hasInstallments ? ' e a parcela' : ''} é a aplicada
                            — a ordem importa.
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export default function FeesTab() {
    const [policy, setPolicy] = useState<BillingFeePolicy>(DEFAULT_POLICY)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        void getBillingFeePolicy()
            .then(response => {
                if (response.success && response.result) {
                    // O backend já devolve a v2 (converte a configuração antiga na
                    // leitura), então aqui não há tratamento de versão.
                    setPolicy({ ...DEFAULT_POLICY, ...response.result })
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [])

    const save = async () => {
        setSaving(true)
        try {
            const response = await updateBillingFeePolicy(policy)
            if (response.success) toast.success('Taxas salvas')
            else toast.error(response.error || 'Erro ao salvar as taxas')
        } catch {
            toast.error('Erro ao salvar as taxas')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
        <TabBranchActions
            scope="fees"
            label="Taxas"
            disabled={saving}
            buildPayload={() => ({ fees: policy })}
            onImported={payload => {
                if (payload.fees)
                    setPolicy({ ...DEFAULT_POLICY, ...payload.fees })
            }}
        />
        <div className="card bg-base-100 shadow-sm border border-base-300">
            <div className="card-body">
                <h2 className="card-title text-lg">
                    Taxas repassadas ao cliente
                </h2>
                <p className="text-sm text-base-content/70">
                    O valor cobrado do paciente já sai com a taxa embutida — ele
                    nunca vê uma linha de taxa. O que a operadora cobra da empresa
                    está na aba <strong>Taxas Fixas</strong>.
                </p>

                <label className="mt-2 flex cursor-pointer items-center gap-3">
                    <input
                        type="checkbox"
                        className="toggle toggle-secondary"
                        checked={policy.enabled}
                        onChange={e =>
                            setPolicy({ ...policy, enabled: e.target.checked })
                        }
                    />
                    <span className="text-sm">
                        Acrescentar taxas às cobranças geradas
                    </span>
                </label>

                {policy.enabled && (
                    <div className="mt-4 flex flex-col gap-4">
                        <div className="rounded-xl border border-base-300 p-4">
                            <label className="flex cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary toggle-sm"
                                    checked={policy.grossUp}
                                    onChange={e =>
                                        setPolicy({
                                            ...policy,
                                            grossUp: e.target.checked,
                                        })
                                    }
                                />
                                <span className="font-semibold">
                                    Cobrar a taxa por dentro
                                </span>
                            </label>
                            <p className="mt-1 text-xs text-base-content/60">
                                A operadora cobra o percentual sobre o valor que passa
                                nela, não sobre o valor do serviço. Somando por fora, a
                                empresa recebe menos do que o serviço custa: em 4,29%,
                                um procedimento de R$ 8.899,88 é cobrado a R$ 9.281,78 e
                                líquida R$ 8.883,59. Por dentro, é cobrado a R$ 9.298,90
                                e o líquido fecha na base.
                            </p>
                            <p className="mt-1 text-xs text-warning">
                                Ligar aumenta o valor cobrado do paciente nas formas com
                                percentual. Nas de valor fixo, nada muda.
                            </p>
                        </div>

                        {METHODS.map(method => (
                            <MethodCard
                                key={method.key}
                                label={method.label}
                                hasInstallments={method.hasInstallments}
                                value={policy[method.key]}
                                onChange={next =>
                                    setPolicy({ ...policy, [method.key]: next })
                                }
                            />
                        ))}

                        <div className="rounded-xl border border-base-300 p-4">
                            <label className="flex cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary toggle-sm"
                                    checked={policy.payout.enabled}
                                    onChange={e =>
                                        setPolicy({
                                            ...policy,
                                            payout: {
                                                ...policy.payout,
                                                enabled: e.target.checked,
                                            },
                                        })
                                    }
                                />
                                <span className="font-semibold">
                                    Taxa de repasse
                                </span>
                            </label>
                            <p className="mt-1 text-xs text-base-content/60">
                                Cada repasse pago a um parceiro custa uma
                                transferência. Este valor dilui esse custo nas
                                cobranças, em qualquer forma de pagamento.
                            </p>

                            {policy.payout.enabled && (
                                <div className="mt-3 flex flex-wrap gap-3">
                                    <label className="form-control">
                                        <span className="label-text text-xs">
                                            Valor fixo por cobrança
                                        </span>
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm"
                                            value={centsToDisplay(
                                                policy.payout.fixed
                                            )}
                                            placeholder="R$ 0,00"
                                            onChange={e =>
                                                setPolicy({
                                                    ...policy,
                                                    payout: {
                                                        ...policy.payout,
                                                        fixed: parseCurrencyToCents(
                                                            currencyFormat(
                                                                e.target.value
                                                            )
                                                        ),
                                                    },
                                                })
                                            }
                                        />
                                    </label>
                                    <label className="form-control">
                                        <span className="label-text text-xs">
                                            Percentual
                                        </span>
                                        <label className="input input-bordered input-sm flex items-center gap-1">
                                            <input
                                                type="number"
                                                step="0.01"
                                                min={0}
                                                max={100}
                                                className="w-24 bg-transparent outline-none"
                                                value={
                                                    policy.payout.percent ?? ''
                                                }
                                                placeholder="0,00"
                                                onChange={e =>
                                                    setPolicy({
                                                        ...policy,
                                                        payout: {
                                                            ...policy.payout,
                                                            percent:
                                                                parsePercent(
                                                                    e.target
                                                                        .value
                                                                ),
                                                        },
                                                    })
                                                }
                                            />
                                            <span className="text-xs opacity-60">
                                                %
                                            </span>
                                        </label>
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="rounded-xl border border-base-300 p-4">
                            <label className="flex cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary toggle-sm"
                                    checked={policy.anticipation.enabled}
                                    onChange={e =>
                                        setPolicy({
                                            ...policy,
                                            anticipation: {
                                                ...policy.anticipation,
                                                enabled: e.target.checked,
                                            },
                                        })
                                    }
                                />
                                <span className="font-semibold">
                                    Repassar a antecipação
                                </span>
                            </label>
                            <p className="mt-1 text-xs text-base-content/60">
                                Só faz sentido quando a empresa antecipa os
                                recebimentos. O valor é proporcional aos dias
                                que o dinheiro seria adiantado — cartão recebe
                                em 32 dias, Pix cai na hora e não entra.
                            </p>

                            {policy.anticipation.enabled && (
                                <div className="mt-3 flex flex-wrap items-end gap-3">
                                    <div className="join">
                                        <button
                                            type="button"
                                            className={`btn join-item btn-sm ${
                                                policy.anticipation.basis ===
                                                'PROVIDER'
                                                    ? 'btn-secondary'
                                                    : 'btn-outline'
                                            }`}
                                            onClick={() =>
                                                setPolicy({
                                                    ...policy,
                                                    anticipation: {
                                                        ...policy.anticipation,
                                                        basis: 'PROVIDER',
                                                    },
                                                })
                                            }
                                        >
                                            Acompanhar as taxas fixas
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn join-item btn-sm ${
                                                policy.anticipation.basis ===
                                                'CUSTOM'
                                                    ? 'btn-secondary'
                                                    : 'btn-outline'
                                            }`}
                                            onClick={() =>
                                                setPolicy({
                                                    ...policy,
                                                    anticipation: {
                                                        ...policy.anticipation,
                                                        basis: 'CUSTOM',
                                                    },
                                                })
                                            }
                                        >
                                            Percentual próprio
                                        </button>
                                    </div>

                                    {policy.anticipation.basis === 'CUSTOM' && (
                                        <label className="form-control">
                                            <span className="label-text text-xs">
                                                Percentual ao mês
                                            </span>
                                            <label className="input input-bordered input-sm flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min={0}
                                                    max={100}
                                                    className="w-24 bg-transparent outline-none"
                                                    value={
                                                        policy.anticipation
                                                            .monthlyPercent ??
                                                        ''
                                                    }
                                                    placeholder="0,00"
                                                    onChange={e =>
                                                        setPolicy({
                                                            ...policy,
                                                            anticipation: {
                                                                ...policy.anticipation,
                                                                monthlyPercent:
                                                                    parsePercent(
                                                                        e.target
                                                                            .value
                                                                    ),
                                                            },
                                                        })
                                                    }
                                                />
                                                <span className="text-xs opacity-60">
                                                    % a.m.
                                                </span>
                                            </label>
                                        </label>
                                    )}
                                </div>
                            )}
                        </div>

                        <p className="flex items-start gap-2 text-xs text-base-content/50">
                            <MdInfoOutline className="mt-0.5 shrink-0 text-sm" />
                            O total com a taxa embutida aparece para o operador
                            na tela de agendamento, por forma de pagamento —
                            para o valor combinado com o paciente ser o mesmo da
                            cobrança.
                        </p>
                    </div>
                )}

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="btn btn-secondary gap-2"
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>
        </div>
        </div>
    )
}
